import React, { useMemo } from 'react';
import { Alert, Linking, Platform, ScrollView, TouchableOpacity, View } from 'react-native'

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import { Image } from 'expo-image';
import IcomoonIcon from '../../../../components/IcomoonIcon';
import { Illustration } from '../../../../components/Illustration';
import { Icon } from '../../../../components/Icon';
import Header from '../../../../navigation/Header';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../../utils/api';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import useActiveAdminCommunity from '@/hooks/useActiveAdminCommunity';


export default function AnnouncementDetailScreen({ navigation, route }) {
  const { announcementId } = route.params
  const { communityId } = useActiveAdminCommunity()
  const queryClient = useQueryClient()

  const { isLoading, error, data: announcement, refetch } = useQuery({
    queryKey: ['community.announcement', announcementId],
    queryFn: () => api.get(`/communities/${communityId}/announcements/${announcementId}`).then(response => response.data),
  })

  function deleteAnnouncement() {
    api.delete(`/communities/${communityId}/announcements/${announcementId}`)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['community.announcements', communityId] })
        navigation.goBack()
      })
      .catch(() => Alert.alert('Error', 'No se pudo eliminar el comunicado'))
  }

  function confirmDelete() {
    const message = '¿Eliminar este comunicado? Esta acción no se puede deshacer.'
    if (Platform.OS === 'web') {
      if (window.confirm(message)) deleteAnnouncement()
      return
    }
    Alert.alert('Eliminar comunicado', message, [
      { text: 'Cancelar', style: 'cancel' },
      { text: 'Eliminar', style: 'destructive', onPress: deleteAnnouncement },
    ])
  }

  const dateStr = announcement
    ? dayjs(announcement.created_at).locale('es').format('D [de] MMMM [del] YYYY [a las] HH:mm')
    : ''

  const seenUnits = announcement?.seen_units ?? []
  const recipientsCount = announcement?.recipients_count ?? 0

  // Viviendas que vieron el comunicado, agrupadas por calle (chips separados por grupo).
  const seenGroups = useMemo(() => {
    const groups = {}
    seenUnits.forEach(unit => {
      const street = unit.street_name || 'Sin calle'
      ;(groups[street] = groups[street] || []).push(unit)
    })
    return Object.keys(groups).sort().map(street => ({ street, units: groups[street] }))
  }, [seenUnits])

  return (
    <View style={tw`flex-1 bg-background`}>
      <Header showBackButton style={tw`bg-transparent`}>
        <View style={tw``}>
          <Text style={tw`text-lg text-secondary`}>{announcement ? announcement.title : 'Cargando...'}</Text>
        </View>
      </Header>

      <ScrollView contentContainerStyle={tw`px-6 py-4 pb-16`}>
        <Text style={tw`text-gray-400`}>{dateStr}</Text>
        <View style={tw`border-b border-gray-200 my-3`} />

        <Text style={tw`text-gray-600`}>{announcement?.text}</Text>

        {
          announcement?.files?.length > 0 &&
            <>
              <Text style={tw`text-secondary font-600 mt-6 mb-2`}>Archivos adjuntos</Text>
              {
                announcement.files.map(file =>
                  <TouchableOpacity
                    key={file.uuid}
                    style={tw`row justify-between bg-white rounded-lg mb-2 border border-gray-200`}
                    onPress={() => Linking.openURL(file.url)}
                  >
                    {
                      file.file_type == "pdf" ?
                        <View style={tw`row py-3 px-4`}>
                          <Illustration name="pdfIcon" width={48} height={48} style={tw`mr-4`} />
                          <Text style={tw`flex-1 text-gray-500`}>{file.name}</Text>
                          <IcomoonIcon name='chevron-right' color={tw.color('secondary')} style={tw`ml-2 -mt-1`} />
                        </View>
                        :
                        <Image source={file.url} style={tw`h-40 w-full`} />
                    }
                  </TouchableOpacity>
                )
              }
            </>
        }

        {/* Visto por */}
        <View style={tw`bg-white rounded-2xl border border-gray-200 mt-6 overflow-hidden`}>
          <View style={tw`row justify-between bg-gray-100 border-b border-gray-200 px-4 py-3`}>
            <Text style={tw`text-secondary font-600`}>Visto por</Text>
            <View style={tw`row items-center`}>
              <Icon name="navViviendas" variant="active" size={18} style={tw`mr-2`} />
              <Text style={tw`text-secondary font-700`}>{seenUnits.length}/{recipientsCount}</Text>
              <Text style={tw`text-gray-400 ml-1`}>viviendas</Text>
            </View>
          </View>

          <View style={tw`px-4 py-4`}>
            {
              seenUnits.length === 0
                ? <Text style={tw`text-gray-400 text-center py-2`}>Aún no lo ha visto ninguna vivienda</Text>
                : seenGroups.map(group =>
                    <View key={group.street} style={tw`mb-3`}>
                      <Text style={tw`text-secondary font-600 mb-2`}>{group.street}</Text>
                      <View style={tw`row flex-wrap`}>
                        {
                          group.units.map(unit =>
                            <View key={unit.uuid} style={tw`bg-gray-100 rounded-lg px-3 py-2 mr-2 mb-2`}>
                              <Text style={tw`text-gray-600`}>{unit.name}</Text>
                            </View>
                          )
                        }
                      </View>
                    </View>
                  )
            }
          </View>
        </View>

        <TouchableOpacity
          style={tw`row justify-center border border-red rounded-full py-3 mt-6`}
          onPress={confirmDelete}
        >
          <Icon name="delete" size={16} color={tw.color('red')} style={tw`mr-2`} />
          <Text style={tw`text-red font-600`}>Eliminar comunicado</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
}
